/**
 * Agent access (MCP) metadata for the AI titlebar button.
 *
 * WINBIT32's "PowerShell moment": anything the site can do, an agent
 * should be able to do through the public MCP endpoint. This module maps
 * each program to the tool families it corresponds to so the AI button
 * can show the MCP command surface for the window it sits on.
 *
 * Keep TOOL_GROUPS in step with the deployed catalogue (winbit32MCP —
 * https://mcp.winbit32.com/mcp, tool prefix `winbit32_`).
 */

export const MCP_SERVER_NAME = 'winbit32';
export const MCP_ENDPOINT_URL = 'https://mcp.winbit32.com/mcp';
export const MCP_HEALTH_URL = 'https://mcp.winbit32.com/health';
export const MCP_REPO_URL = 'https://github.com/FungeLLC/winbit32MCP';

const HEALTH_CHECK_TIMEOUT_MS = 4000;

/**
 * Deployed tool families. `tools` lists live tool names (without prefix
 * duplication — they appear on the wire exactly as written here).
 * `example` is one worked invocation for the family, shown to the user as
 * "ask your agent to …" — args match the live tool input schemas.
 */
export const TOOL_GROUPS = {
	q: {
		title: 'Penny Oracle (free)',
		blurb: 'One question in, one answer out: prices, fees, chain heights.',
		tools: ['winbit32_q'],
		example: {
			tool: 'winbit32_q',
			args: { question: 'zec/height' },
			does: 'ask a live privacy-chain fact (price, fee, height, mempool)',
		},
	},
	zecScan: {
		title: 'Zcash view-key scanning',
		blurb: 'Scan jobs against the wallet-scanner with a UFVK (view-only), list UTXOs, broadcast raw transactions. No spend keys involved.',
		tools: [
			'winbit32_zec_scan_start',
			'winbit32_zec_scan_status',
			'winbit32_zec_scan_cancel',
			'winbit32_zec_utxos',
			'winbit32_zec_broadcast',
		],
		example: {
			tool: 'winbit32_zec_scan_start',
			args: { ufvk: 'uview1…' },
			does: 'start a view-only Orchard scan of a Zcash UFVK, then poll status',
		},
	},
	xmrScan: {
		title: 'Monero view-key scanning',
		blurb: 'Output scanning with a Monero view key — same job pattern as Zcash.',
		tools: [
			'winbit32_xmr_scan_start',
			'winbit32_xmr_scan_status',
			'winbit32_xmr_scan_cancel',
		],
		example: {
			tool: 'winbit32_xmr_scan_start',
			args: { address: '4…', viewKey: '…' },
			does: 'start a view-only Monero scan from an address + private view key',
		},
	},
	phrase: {
		title: 'Phrase hygiene (local, offline)',
		blurb: 'BIP-39 validation, final-word search and generation, plus a self-test of the randomness that generation depends on. Generated phrases mix two independent CSPRNG draws with any entropy you supply, and generation is refused outright if the self-test fails. Nothing leaves the server process; nothing is stored.',
		tools: [
			'winbit32_phrase_validate',
			'winbit32_phrase_complete',
			'winbit32_phrase_generate',
			'winbit32_entropy_selftest',
		],
		example: {
			tool: 'winbit32_phrase_validate',
			args: { phrase: 'abandon abandon … art' },
			does: 'check a BIP-39 phrase (word membership, length, checksum)',
		},
	},
	shamir: {
		title: 'Shamir split / combine (local, offline)',
		blurb: 'Split secret material into m-of-n shares and recombine. Nothing is stored.',
		tools: ['winbit32_shamir_split', 'winbit32_shamir_combine'],
		example: {
			tool: 'winbit32_shamir_split',
			args: { secretHex: 'deadbeef…', shares: 5, threshold: 3 },
			does: 'split a hex secret into 5 shares, any 3 of which reconstruct it',
		},
	},
	watch: {
		title: 'Private balance watches',
		blurb: 'Register a view key for ongoing shielded balance watching, top up, query history, derive view keys.',
		tools: [
			'winbit32_private_watch_info',
			'winbit32_private_watch_create',
			'winbit32_private_watch_topup',
			'winbit32_private_watch_topup_crypto',
			'winbit32_private_watch_historical',
			'winbit32_private_watch_derive_viewkey',
		],
		example: {
			tool: 'winbit32_private_watch_create',
			args: { chain: 'zcash', address: 'u1…', viewKey: 'uview1…', webhookUrl: 'https://…' },
			does: 'watch an address for inbound shielded payments and get signed webhooks',
		},
	},
	paywall: {
		title: 'Paywall / x402',
		blurb: 'Pricing and settlement info for the paid surfaces.',
		tools: ['winbit32_paywall_info'],
		example: {
			tool: 'winbit32_paywall_info',
			args: {},
			does: 'see what the paid endpoints cost and how settlement works',
		},
	},
	zecAmounts: {
		title: 'Zcash amount privacy (free)',
		blurb: 'Pick shield/deshield amounts that blend in with the crowd, plan splitting a large amount into ordinary-sized pieces, and read the live popular-amount index. No wallet, view key, or payment.',
		tools: [
			'winbit32_zec_amount_advice',
			'winbit32_zec_split_plan',
			'winbit32_zec_popular_amounts',
		],
		example: {
			tool: 'winbit32_zec_amount_advice',
			args: { amountZec: 1.0, action: 'deshield' },
			does: 'check whether an amount blends in and suggest nearby blend-in amounts',
		},
	},
	zecBus: {
		title: 'Zcash mixing "bus" (free, opt-in)',
		blurb: 'Coordinate a non-custodial mixing "bus": list boarding buses, reserve a seat to leave the Zcash pool alongside others at the same blend-in amount, route and short window, then board or leave. The gateway holds no funds and no keys — every rider broadcasts their own swap, so the seat count is an upper bound on the anonymity set.',
		tools: [
			'winbit32_zec_bus_list',
			'winbit32_zec_bus_join',
			'winbit32_zec_bus_status',
			'winbit32_zec_bus_board',
			'winbit32_zec_bus_leave',
		],
		example: {
			tool: 'winbit32_zec_bus_join',
			args: { to: 'BTC.BTC', amount: 1, minPassengers: 5 },
			does: 'reserve a seat on a bus leaving the Zcash pool to BTC at a 1 ZEC blend-in amount',
		},
	},
	nfptMarket: {
		title: 'NFPT marketplace (free reads)',
		blurb: 'Browse secresea NFPT collections and listings. Buying is a shielded-ZEC payment from the gateway vault — the human co-signs (same as make_payment); royalty-splitting listings are not yet payable in-tool.',
		tools: [
			'winbit32_nfpt_list_collections',
			'winbit32_nfpt_listings',
		],
		example: {
			tool: 'winbit32_nfpt_list_collections',
			args: {},
			does: 'list public NFPT collections, then inspect a listing with nfpt_listings',
		},
	},
	board: {
		title: 'Notice boards (free reads)',
		blurb: 'Public notice boards: list boards, read threads, post and reply. Boosting a post settles a small x402 payment.',
		tools: [
			'winbit32_board_list',
			'winbit32_board_read',
			'winbit32_board_post',
			'winbit32_board_reply',
			'winbit32_board_boost',
		],
		example: {
			tool: 'winbit32_board_list',
			args: {},
			does: 'list the public notice boards and their posting/boost pricing',
		},
	},
};

const ALL_GROUP_KEYS = Object.keys(TOOL_GROUPS);

/**
 * Program → tool families. Programs not listed fall back to the full
 * catalogue (every window gets the AI button; specificity is progressive).
 */
const PROG_TOOL_GROUPS = {
	// Zcash suite
	'zcashwzrd.exe': ['zecScan', 'watch', 'q'],
	'zcashcli.exe': ['zecScan', 'watch', 'q'],
	'zcashtxi.exe': ['zecScan', 'q'],
	'zcashrecv.exe': ['zecScan', 'watch', 'q'],
	'zcashtest.exe': ['zecScan', 'q'],
	// zcashamounts.exe merged into the Bus Station wizard (step 1) —
	// zcashbus.exe carries both tool families now.
	'zcashbus.exe': ['zecBus', 'zecAmounts', 'q'],
	'purse.exe': ['zecScan', 'watch', 'q'],
	'nfpt.exe': ['zecScan', 'nfptMarket', 'q'],
	// Monero suite
	'monerorecv.exe': ['xmrScan', 'watch', 'q'],
	'monerosend.exe': ['xmrScan', 'watch', 'q'],
	'monerotxi.exe': ['xmrScan', 'q'],
	// SecTools
	'sectools.exe': ['phrase', 'shamir'],
	'split.exe': ['shamir'],
	'unsplit.exe': ['shamir'],
	'sss.exe': ['shamir', 'phrase'],
	'phunter.exe': ['phrase'],
	'entropy.exe': ['phrase'],
	// The test lab runs entirely in the browser against whatever bytes it
	// is given, so it needs no server tools of its own — but an agent
	// asked to check a generator should be pointed at the same self-test
	// the phrase tools expose.
	'rngtest.exe': ['phrase'],
	// Vault / address tooling
	'vtools.exe': ['phrase', 'shamir'],
	'createvault.exe': ['phrase', 'shamir'],
	'addrgen.exe': ['phrase', 'q'],
	'addrfind.exe': ['q'],
	// Messenger — the in-app AI chat itself talks to this whole catalogue.
	'messenger.exe': ALL_GROUP_KEYS,
	// Gopher — discovery browser that also buys listed NFPTs. Reads are
	// always on; buy tools appear once the gateway vault is configured.
	'gopher.exe': ['nfptMarket', 'q', 'paywall'],
	// Money Manager & friends
	'winbit32.exe': ['q', 'watch', 'paywall'],
	'portfolio.exe': ['q', 'watch'],
	'exchange.exe': ['q'],
	'exora.exe': ['q'],
	'send.exe': ['q'],
	'swappots.exe': ['q'],
	'crypto.exe': ['q'],
	'x402pay.exe': ['q', 'paywall'],
	'cosign.exe': ['zecScan', 'q'],
};

const PROG_NOTES = {
	'cosign.exe': 'Agents prepare transactions with a vault share, then present the cosign QR — this app is the human half of the signature.',
	'nfpt.exe': 'Agents can browse collections and listings, and scan inscriptions with a view key; purchases and transfers still need a co-sign.',
};

/**
 * One-line "what an agent can do with THIS app" headline. Keyed by
 * progName; falls back to the primary tool family's blurb when absent so
 * every specific window still leads with relevant copy.
 */
const PROG_SUMMARY = {
	'nfpt.exe': 'Scan for the NFPTs an address holds with a view key, then read and verify them. Browse the marketplace listings; minting, transfers and buys still need a co-sign.',
	'cosign.exe': 'The human half of an agent-built transaction: the agent prepares a spend from one vault share and you approve it here by co-signing.',
	'sectools.exe': 'Seed-phrase hygiene and Shamir secret-sharing — entirely local and offline, nothing leaves the machine.',
	'split.exe': 'Split secret material into m-of-n Shamir shares an agent can help you manage.',
	'unsplit.exe': 'Recombine Shamir shares back into the original secret.',
	'sss.exe': 'Connect by rebuilding your seed from Shamir Secret Sharing (SSS) shares — reconstructed locally, never a threshold signature.',
	'phunter.exe': 'Recover a BIP-39 phrase with a missing or mistyped word.',
	'zcashwzrd.exe': 'Drive Zcash view-only scanning and private balance watches from your agent — no spend keys involved.',
	'zcashcli.exe': 'Script Zcash view-key scans and balance watches from your agent.',
	'zcashrecv.exe': 'Watch a Zcash address for inbound shielded payments and get signed webhooks.',
	'zcashbus.exe': 'Plan blend-in shield/deshield amounts (round-trip self-dox check, split planning — free, no wallet or keys) and coordinate a non-custodial mixing "bus": leave the Zcash pool alongside others at the same amount, route and short window. The coordinator never holds funds or keys; you broadcast your own swap.',
	'purse.exe': 'The beginner-friendly shielded Zcash wallet (yes, the pink one). Agents can scan balances and watch for payments with the purse\u2019s "Purse-Cam" (a viewing key) — spending still needs the Magic Words or a Friendship Locket (vault) co-sign, which never leave the window.',
	'monerorecv.exe': 'Watch a Monero address for inbound payments via its view key and get signed webhooks.',
	'monerosend.exe': 'Scan Monero balances view-only; spends still need a signature.',
	'addrgen.exe': 'Generate and validate addresses and seed phrases with an agent.',
	'addrfind.exe': 'Ask live privacy-chain facts to help locate or check addresses.',
	'messenger.exe': 'Chat with AI agents that can drive the winbit32 desktop and this whole MCP toolset — bring your own model key or use the hosted gateway.',
	'gopher.exe': 'Browse agent/MCP discovery cards, then buy a listed NFPT over the same Gopher window: list collections, inspect listings, and (when the gateway vault is configured) start a co-signed shielded-ZEC purchase.',
	'winbit32.exe': 'Money Manager: ask live facts, run private balance watches, and check what the paid surfaces cost.',
	'exchange.exe': 'Quote swaps with live chain facts (swap execution still runs in the app).',
	'exora.exe': 'Pull live chain facts to plan batch operations.',
	'x402pay.exe': 'Pay any x402 endpoint in USDC straight from your own vault — the vault signs the payment, keys never leave your shares. Top up from ZEC when short.',
};

/**
 * Agent config snippet (Cursor / Claude style `mcpServers` block).
 */
export const buildAgentConfigSnippet = () =>
	JSON.stringify(
		{ mcpServers: { [MCP_SERVER_NAME]: { url: MCP_ENDPOINT_URL } } },
		null,
		2,
	);

/** Expand a list of group keys into full group objects (with their key). */
const expandGroups = (keys) => keys.map((key) => ({ key, ...TOOL_GROUPS[key] }));

/** The full catalogue as group objects — the "general" view. */
export const getAllToolGroups = () => expandGroups(ALL_GROUP_KEYS);

/** Total number of distinct tools across the whole catalogue. */
export const getToolCount = () =>
	new Set(ALL_GROUP_KEYS.flatMap((key) => TOOL_GROUPS[key].tools)).size;

/**
 * Resolve the agent-access info for a program.
 *
 * For a mapped program this is deliberately *focused*: a one-line summary
 * of what an agent does with this app, that app's tool families, and a
 * worked example invocation. The full catalogue ("the general stuff") is
 * always available via `allGroups` so the dialog can link to it.
 *
 * @param {string} [progName] e.g. 'nfpt.exe'
 * @returns {{ endpoint: string, serverName: string, configSnippet: string,
 *   groups: Array<{key: string, title: string, blurb: string, tools: string[]}>,
 *   allGroups: Array<object>, toolCount: number, specific: boolean,
 *   summary: string|null, example: object|null, note: string|null }}
 */
export const getAgentAccessInfo = (progName) => {
	const specific = Boolean(PROG_TOOL_GROUPS[progName]);
	const groupKeys = PROG_TOOL_GROUPS[progName] || ALL_GROUP_KEYS;
	const groups = expandGroups(groupKeys);
	// Headline + worked example are driven by the app's PRIMARY family
	// (the first in its map), with a per-program override for the headline.
	const primary = groups[0] || null;
	const summary = specific
		? (PROG_SUMMARY[progName] || (primary ? primary.blurb : null))
		: null;
	const example = specific && primary ? (primary.example || null) : null;
	return {
		endpoint: MCP_ENDPOINT_URL,
		serverName: MCP_SERVER_NAME,
		configSnippet: buildAgentConfigSnippet(),
		groups,
		allGroups: getAllToolGroups(),
		toolCount: getToolCount(),
		specific,
		summary,
		example,
		note: PROG_NOTES[progName] || null,
	};
};

/**
 * Ping the MCP endpoint's health route. Resolves true/false; never throws
 * (network/CORS failures read as offline so the dialog can say so).
 * @param {typeof fetch} [fetchImpl] injectable for tests
 */
export const checkAgentEndpoint = async (fetchImpl) => {
	const doFetch = fetchImpl || (typeof fetch !== 'undefined' ? fetch : null);
	if (!doFetch) return false;
	const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
	const timer = controller ? setTimeout(() => controller.abort(), HEALTH_CHECK_TIMEOUT_MS) : null;
	try {
		const res = await doFetch(MCP_HEALTH_URL, {
			method: 'GET',
			signal: controller?.signal,
		});
		return Boolean(res && res.ok);
	} catch (e) {
		return false;
	} finally {
		if (timer) clearTimeout(timer);
	}
};
